import React, { useState } from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCommentSms, faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { CommunicoreEnvironmentSummary, getCommunicoreSummary, OutgoingStats, SesStats } from '@/services/communicore';
import theme from './Communicore.module.scss';

const stageLabel: Record<string, string> = { test: 'Test', uat: 'UAT', prod: 'Prod' };
const label = (stage: string) => stageLabel[stage] ?? stage;
// AWS warns at a 5% bounce rate and can pause all sending at 10%.
const BOUNCE_WARN = 0.05;
const ERROR_ROWS = 5;

const fmt = (n: number) => n.toLocaleString('en-GB');

/** 24h counts per environment above the tables; the tables themselves always show this MacGyver's environment. */
const CommunicoreSummary: React.FC = () => {
    const [summary, , reload, isLoading] = useAsyncStateWithIsLoading(getCommunicoreSummary, [], null);
    const [picked, setPicked] = useState<string | null>(null);

    if (!summary) {
        return <p className={theme.summaryMuted}>{isLoading ? 'Loading summary…' : 'Summary unavailable.'}</p>;
    }
    const envs = summary.environments;
    const env = envs.find((e) => e.stage === picked) ?? envs[0];

    return (
        <section className={theme.summary} aria-label="Last 24 hours">
            <div className={theme.summaryHead}>
                <span className={theme.summaryTitle}>Last 24 hours</span>
                {envs.length > 1 &&
                    envs.map((e, i) => (
                        <button
                            key={e.stage}
                            type="button"
                            className={theme.envChip}
                            aria-pressed={e.stage === env?.stage}
                            onClick={() => setPicked(e.stage)}>
                            {label(e.stage)}
                            {i === 0 && ' (this)'}
                            {needsAttention(e) && <span className={theme.alertDot} title="Needs attention" />}
                        </button>
                    ))}
                <Button size="xs" variant="tertiary" icon={faArrowsRotate} onClick={reload} disabled={isLoading}>
                    Refresh
                </Button>
            </div>

            {env?.error && <p className={theme.summaryError}>{env.error}</p>}

            <div className={theme.summaryTiles}>
                <Outgoing name="Emails out" icon={faEnvelope} stats={env?.stats?.outgoingEmails} />
                <Outgoing name="SMS out" icon={faCommentSms} stats={env?.stats?.outgoingSms} />
                <div className={theme.tile}>
                    <div className={theme.tileLabel}>Emails in</div>
                    <div className={theme.tileValue}>{env?.stats ? fmt(env.stats.receivedEmails) : '—'}</div>
                </div>
                <Stuck env={env} />
                <Ses ses={summary.ses} />
            </div>

            {env?.stats && <TopErrors env={env} />}
        </section>
    );
};

const needsAttention = (e: CommunicoreEnvironmentSummary) =>
    !!e.error || !!e.stats?.outgoingEmails.stuck || !!e.stats?.outgoingSms.stuck;

const Outgoing: React.FC<{ name: string; icon: typeof faEnvelope; stats: OutgoingStats | undefined }> = ({
    name,
    icon,
    stats,
}) => (
    <div className={theme.tile}>
        <div className={theme.tileLabel}>
            <FontAwesomeIcon icon={icon} /> {name}
        </div>
        <div className={theme.tileValue}>{stats ? fmt(stats.sent) : '—'}</div>
        {stats && (
            <div className={stats.failed ? theme.bad : theme.summaryMuted}>
                {fmt(stats.failed)} failed · {fmt(stats.pending)} pending
            </div>
        )}
    </div>
);

const Stuck: React.FC<{ env: CommunicoreEnvironmentSummary | undefined }> = ({ env }) => {
    const stuck = env?.stats ? env.stats.outgoingEmails.stuck + env.stats.outgoingSms.stuck : null;
    return (
        <div className={theme.tile}>
            <div className={theme.tileLabel}>Stuck</div>
            <div className={`${theme.tileValue} ${stuck ? theme.bad : ''}`}>{stuck ?? '—'}</div>
            <div className={theme.summaryMuted}>pending over 15 min</div>
        </div>
    );
};

const Ses: React.FC<{ ses: SesStats }> = ({ ses }) => (
    <div className={theme.tile} title="SES covers every environment: it's one per AWS account">
        <div className={theme.tileLabel}>SES bounce rate</div>
        <div className={`${theme.tileValue} ${(ses.bounceRate ?? 0) >= BOUNCE_WARN ? theme.bad : ''}`}>
            {ses.error || ses.bounceRate == null ? '—' : `${(ses.bounceRate * 100).toFixed(1)}%`}
        </div>
        <div className={theme.summaryMuted}>
            {ses.error ? 'unavailable' : `${fmt(ses.bounced)} bounced of ${fmt(ses.sent)}, all envs`}
        </div>
    </div>
);

const TopErrors: React.FC<{ env: CommunicoreEnvironmentSummary }> = ({ env }) => {
    const errors = [
        ...env.stats!.outgoingEmails.topErrors.map((e) => ({ ...e, icon: faEnvelope })),
        ...env.stats!.outgoingSms.topErrors.map((e) => ({ ...e, icon: faCommentSms })),
    ]
        .sort((a, b) => b.count - a.count)
        .slice(0, ERROR_ROWS);
    if (errors.length === 0) return null;
    return (
        <ul className={theme.topErrors}>
            {errors.map((e) => (
                <li key={`${e.icon.iconName}-${e.error}`}>
                    <FontAwesomeIcon icon={e.icon} fixedWidth className={theme.summaryMuted} />
                    <span className={theme.errorText} title={e.error}>
                        {e.error}
                    </span>
                    <span className={theme.errorCount}>×{e.count}</span>
                </li>
            ))}
        </ul>
    );
};

export default CommunicoreSummary;
